import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, Popover } from "@mui/material";
import {
    Star,
    MoreVertical,
    Users,
    Trash2,
    Sprout,
    FileText,
    LayoutGrid,
    Layers,
    type LucideIcon,
} from "lucide-react";
import { useDeleteNotebook } from "../tars-hooks/useDeleteNotebook";
import {
    notebookLastUpdatedLabel,
    type NotebookCardStats,
    type NotebookListItem,
} from "../tars-hooks/useGetNotebooks";
import {
    TARS_NOTEBOOK_CARD_CANVASES_LABEL,
    TARS_NOTEBOOK_CARD_FACETS_LABEL,
    TARS_NOTEBOOK_CARD_HEIGHT_PX,
    TARS_NOTEBOOK_CARD_MIN_WIDTH_PX,
    TARS_NOTEBOOK_CARD_UPDATED_LABEL,
} from "../tars-constants";
import { iconComponents } from "./utils";
import { NotebookCardOwnerAvatar } from "./NotebookCardOwnerAvatar";

const notebookCardSizeStyle = {
    height: TARS_NOTEBOOK_CARD_HEIGHT_PX,
    minWidth: TARS_NOTEBOOK_CARD_MIN_WIDTH_PX,
};

function NotebookCardStatChip({
    icon: Icon,
    value,
    label,
    loading = false,
}: {
    icon: LucideIcon;
    value: number;
    label: string;
    loading?: boolean;
}) {
    return (
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#f3f4f6] px-2.5 py-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                <Icon size={13} className="text-[#374151]" strokeWidth={2.25} />
            </div>
            <div className="min-w-0">
                <p className="text-[14px] font-bold leading-none text-[#1b1b1d]">
                    {loading ? "—" : value}
                </p>
                <p className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">
                    {label}
                </p>
            </div>
        </div>
    );
}

const CreateNotebookCard = ({ onClick }) => (
    <div
        onClick={onClick}
        style={notebookCardSizeStyle}
        className="group flex cursor-pointer flex-col items-center justify-center rounded-tl-3xl border border-gray-200 bg-white transition-colors hover:bg-[#A5F455]"
    >
        <div className="relative mb-4 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gray-100 bg-white shadow-lg transition-transform group-hover:scale-110">
                <FileText
                    size={28}
                    className="text-gray-400 group-hover:text-black"
                />
            </div>
        </div>
        <span className="text-[15px] font-bold text-[#1b1b1d]">
            Create a Notebook
        </span>
    </div>
);

const NotebookCard = ({
    notebook,
    isStarred,
    onStarClick,
    currentUserId,
    currentUserProfileImageS3Key,
    currentUserName,
    stats,
    statsLoading = false,
    lastTouchedMs,
    detailModifiedat = null,
}: {
    notebook: NotebookListItem;
    isStarred?: boolean;
    onStarClick?: (id: number) => void;
    currentUserId: number;
    currentUserProfileImageS3Key: string | null;
    currentUserName: string;
    stats?: NotebookCardStats;
    statsLoading?: boolean;
    lastTouchedMs?: number;
    detailModifiedat?: string | null;
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const { mutate: deleteNotebook, isLoading } = useDeleteNotebook();

    const isMenuOpen = Boolean(anchorEl);
    const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        deleteNotebook(notebook.id);
        handleMenuClose();
    };

    const iconName = notebook.more?.icon || notebook.icon || "Sprout";

    const IconComponent = iconComponents[iconName] || Sprout;

    const ownerId = Number(notebook.owner ?? notebook.user_id);
    const safeOwnerId = Number.isFinite(ownerId) ? ownerId : currentUserId;
    

    return (
        <div
            onClick={() => {
            if (!isMenuOpen) {
                navigate(`/tars/${notebook.id}`);
            }
        }}
            style={notebookCardSizeStyle}
            className="relative flex cursor-pointer flex-col border bg-white p-5 transition-all hover:border-[#A5F455]"
        >
            <div className="mb-4 flex items-start justify-between">
                <div
                    className="flex h-8 w-8 items-center justify-center rounded-lg"
                    style={{
                        backgroundColor: notebook.more?.color || "#A5F455",
                    }}
                >
                    <IconComponent size={16} color="#000000" strokeWidth={1}/>
                </div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onStarClick?.(notebook.id);
                    }}
                    className="rounded p-0.5 transition-colors"
                    aria-label={isStarred ? "Unstar notebook" : "Star notebook"}
                >
                    <Star
                        size={22}
                        className={
                            isStarred
                                ? "fill-yellow-400 text-yellow-400"
                                : "cursor-pointer text-gray-300 hover:fill-yellow-400 hover:text-yellow-400"
                        }
                    />
                </button>
            </div>

            <div className="min-h-0 flex-1">
                <h3 className="mb-2 line-clamp-2 text-[18px] font-bold leading-tight text-[#1b1b1d]">
                    {notebook.name}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                    {(notebook.tags ?? []).map((tag: string, idx: number) => (
                        <span
                            key={idx}
                            className="rounded-md px-2 py-1 text-[12px] font-bold min-w-[40px] text-center capitalize"
                            style={{
                                backgroundColor:
                                    notebook.more?.color || "#A5F455",
                                color: "#000000",
                            }}
                        >
                            {tag}
                        </span>
                    ))}
                </div>
                <div className="mt-3 flex gap-2">
                    <NotebookCardStatChip
                        icon={LayoutGrid}
                        value={stats?.canvasCount ?? 0}
                        label={TARS_NOTEBOOK_CARD_CANVASES_LABEL}
                        loading={statsLoading}
                    />
                    <NotebookCardStatChip
                        icon={Layers}
                        value={stats?.facetCount ?? 0}
                        label={TARS_NOTEBOOK_CARD_FACETS_LABEL}
                        loading={statsLoading}
                    />
                </div>
            </div>

            <div className="mt-auto flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 pt-3">
                <div className="flex min-w-0 items-center gap-2">
                    <NotebookCardOwnerAvatar
                        ownerId={safeOwnerId}
                        currentUserId={currentUserId}
                        profileImageS3Key={currentUserProfileImageS3Key}
                        userName={currentUserName}
                    />
                    <p className="truncate text-[11px] font-medium text-gray-400">
                        {TARS_NOTEBOOK_CARD_UPDATED_LABEL}{" "}
                        {notebookLastUpdatedLabel(notebook, {
                            lastTouchedMs,
                            detailModifiedat,
                        })}
                    </p>
                </div>

                <IconButton
                    onClick={handleMenuOpen}
                    size="small"
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen}
                    aria-controls={isMenuOpen ? "notebook-card-menu" : undefined}
                    sx={{
                        color: "#9ca3af",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                >
                    <MoreVertical size={18} />
                </IconButton>

                <Popover
                    id="notebook-card-menu"
                    open={isMenuOpen}
                    anchorEl={anchorEl}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    slotProps={{
                        paper: {
                            onClick: (e) => e.stopPropagation(),
                            className:
                                "w-36 rounded-xl border border-gray-100 p-2 shadow-2xl",
                        },
                    }}
                    disableScrollLock
                >
                    <Button
                        fullWidth
                        startIcon={<Users size={14} />}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#374151",
                            "&:hover": { backgroundColor: "#f9fafb" },
                        }}
                    >
                        View Team
                    </Button>
                    <Button
                        fullWidth
                        startIcon={<Trash2 size={14} />}
                        onClick={handleDelete}
                        disabled={isLoading}
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#ef4444",
                            "&:hover": { backgroundColor: "#fef2f2" },
                            // "&.Mui-disabled": { opacity: 0.5 },
                        }}
                    >
                        Move to Bin
                    </Button>
                </Popover>
            </div>
        </div>
    );
};

export { CreateNotebookCard, NotebookCard };
